import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import {
  LGrid,
  LButton,
  LDialog,
  LFade,
} from "@loffa/gpp-modules";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRefresh, faFilter, faEye } from "@fortawesome/free-solid-svg-icons";
import styles from "./AutoimportLogs.module.css";
import { AuthContext } from "../../../../../../context/AuthContext";
import LoadingOverlay from "src/components/Shared/LoadingOverlay/LoadingOverlay";

const GATEWAY_BASE_URL = process.env.REACT_APP_API_GATEWAY_BASE;

interface AutomationsLog {
  id: number;
  message: string;
  messageTemplate: string;
  level: string;
  timeStamp: string;
  source: string;
}

interface GridCellProps {
  dataItem: AutomationsLog;
}

interface LogColumn {
  field: string;
  title: string;
  width?: number;
  cell?: (props: GridCellProps) => ReactNode;
  filterable?: boolean;
  filter?: string;
  format?: string;
}

function AutoimportLogs() {
  const [logs, setLogs] = useState<AutomationsLog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<string>("All");
  const [selectedLog, setSelectedLog] = useState<AutomationsLog | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  const authContext = useContext(AuthContext);
  const token: string = authContext.authState().accessToken.accessToken;

  const fetchLogs = useCallback(
    async (logLevel?: string) => {
      setLoading(true);
      setError(null);

      try {
        let endpoint = `${GATEWAY_BASE_URL}/datamanager/api/Logs?count=200`;
        if (logLevel && logLevel !== "All") {
          endpoint = `${GATEWAY_BASE_URL}/datamanager/api/Logs/by-level/${logLevel}?count=200`;
        }

        const response = await fetch(endpoint, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch logs: ${response.statusText}`);
        }

        const data: AutomationsLog[] = await response.json();
        setLogs(data);
      } catch (err: any) {
        console.error("Error fetching logs:", err);
        setError(err.message || "Failed to fetch logs");
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleRefresh = useCallback(() => {
    fetchLogs(selectedLevel);
  }, [fetchLogs, selectedLevel]);

  const handleLevelFilterChange = useCallback(
    (level: string) => {
      setSelectedLevel(level);
      fetchLogs(level);
    },
    [fetchLogs]
  );

  const handleViewDetails = useCallback((log: AutomationsLog) => {
    setSelectedLog(log);
    setShowDetailsDialog(true);
  }, []);

  const handleCloseDetailsDialog = useCallback(() => {
    setShowDetailsDialog(false);
    setSelectedLog(null);
  }, []);

  const formatTimestamp = useCallback((timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  }, []);

  const getLevelColor = useCallback((level: string) => {
    const levelLower = level.toLowerCase();
    switch (levelLower) {
      case "error":
        return styles.levelError;
      case "warning":
      case "warn":
        return styles.levelWarning;
      case "information":
      case "info":
        return styles.levelInfo;
      default:
        return styles.levelDefault;
    }
  }, []);

  const columns: LogColumn[] = useMemo(() => {
    return [
      {
        field: "actions",
        title: "Actions",
        width: 50,
        cell: (props: GridCellProps) => (
          <td className={styles.logsActionButtons}>
            <button
              className={styles.viewButton}
              onClick={() => handleViewDetails(props.dataItem)}
              title="View Details"
            >
              <FontAwesomeIcon icon={faEye} />
            </button>
          </td>
        ),
        filterable: false,
      },
      {
        field: "id",
        title: "ID",
        width: 80,
        filterable: true,
      },
      {
        field: "level",
        title: "Level",
        width: 100,
        cell: (props: GridCellProps) => (
          <td>
            <span
              className={`${styles.levelBadge} ${getLevelColor(
                props.dataItem.level
              )}`}
            >
              {props.dataItem.level}
            </span>
          </td>
        ),
        filterable: true,
      },
      {
        field: "timeStamp",
        title: "Timestamp",
        width: 180,
        cell: (props: GridCellProps) => (
          <td>{formatTimestamp(props.dataItem.timeStamp)}</td>
        ),
        filterable: true,
      },
      {
        field: "source",
        title: "Source",
        width: 120,
        cell: (props: GridCellProps) => <td>{props.dataItem.source}</td>,
        filterable: true,
      },
      {
        field: "message",
        title: "Message",
        width: 500,
        cell: (props: GridCellProps) => (
          <td className={styles.messageCell} title={props.dataItem.message}>
            {props.dataItem.message}
          </td>
        ),
        filterable: true,
      },
      {
        field: "messageTemplate",
        title: "Message Template",
        width: 400,
        cell: (props: GridCellProps) => (
          <td
            className={styles.messageTemplateCell}
            title={props.dataItem.messageTemplate}
          >
            {props.dataItem.messageTemplate || "-"}
          </td>
        ),
        filterable: true,
      },
    ];
  }, [formatTimestamp, getLevelColor, handleViewDetails]);

  const loadingPanel = <LoadingOverlay loadingText="Loading logs..." />;

  const logLevels = ["All", "Warning", "Information", "Error"];

  return (
    <div className={styles.automationLogsContainer}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2>Automation Logs</h2>
          <span className={styles.logCount}>
            {!loading && `(${logs.length} logs)`}
          </span>
        </div>
        <div className={styles.headerActions}>
          <div className={styles.levelFilter}>
            <FontAwesomeIcon icon={faFilter} className={styles.filterIcon} />
            <select
              value={selectedLevel}
              onChange={(e) => handleLevelFilterChange(e.target.value)}
              className={styles.levelSelect}
            >
              {logLevels.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
          </div>
          <LButton
            onClick={handleRefresh}
            themeColor="primary"
            className="green"
            disabled={loading}
          >
            <FontAwesomeIcon icon={faRefresh} className={styles.buttonIcon} />
            Refresh
          </LButton>
        </div>
      </div>

      {error && (
        <div className={styles.error}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {loading && loadingPanel}

      <div className={styles.gridContainer}>
        <LGrid
          data={logs}
          columns={columns}
          loading={loading}
          sortable
          filterable
          resizable
          pageable
          style={{ height: "550px" }}
          className={styles.grid}
        />
      </div>

      <LFade transitionEnterDuration={300}>
        {showDetailsDialog && selectedLog && (
          <LDialog
            width="600"
            title="Log Details"
            subtitle={`ID: ${selectedLog.id}`}
            body={
              <div className={styles.logDetailsBody}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Level:</span>
                  <span className={`${styles.levelBadge} ${getLevelColor(selectedLog.level)}`}>
                    {selectedLog.level}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Source:</span>
                  <span className={styles.sourceBadge}>{selectedLog.source}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Timestamp:</span>
                  <span>{formatTimestamp(selectedLog.timeStamp)}</span>
                </div>
                <div className={styles.detailSection}>
                  <span className={styles.detailLabel}>Message:</span>
                  <div className={styles.detailContent}>
                    {selectedLog.message}
                  </div>
                </div>
                {selectedLog.messageTemplate && (
                  <div className={styles.detailSection}>
                    <span className={styles.detailLabel}>Message Template:</span>
                    <div className={styles.detailContentMonospace}>
                      {selectedLog.messageTemplate}
                    </div>
                  </div>
                )}
              </div>
            }
            actionCloseButton={handleCloseDetailsDialog}
            actionOverlay={handleCloseDetailsDialog}
          />
        )}
      </LFade>
    </div>
  );
}

export { AutoimportLogs };
